/**
 * Browser-oriented helpers for building query strings and reading/mutating the current page's URL and form action attribute (used for passthrough, iMIS page-driven scenarios).
 */
export class UrlUtility {
    /**
     * Appends query string parameters to a URL, expanding array values into repeated `key=value` pairs and skipping null/undefined values.
     * @param url base URL or path to append parameters to
     * @param paramsObject key-value map of query parameters; array values are added as repeated parameters, null/undefined values are skipped. Defaults to an empty object (no parameters added).
     * @returns the URL with query parameters appended
     */
    public static getUrlStringWithParams(url: string, paramsObject: any = {}): string {
        let paramsObjectWithDefault = Object.assign({}, paramsObject);
        let urlWithParams: string = url;
        let keys = Object.keys(paramsObjectWithDefault);

        if (keys.length > 0) {
            let addToUrl = function (k, paramsValue) {
                let separator = urlWithParams.indexOf("?") > -1 ? "&" : "?";
                urlWithParams += separator + k + "=" + paramsValue;
            };

            keys.forEach(k => {
                let paramsValue = paramsObjectWithDefault[k];
                if (Array.isArray(paramsValue)) {
                    paramsValue.forEach(function (value) {
                        addToUrl(k, value);
                    });
                } else if (paramsValue != null) {
                    addToUrl(k, paramsValue);
                }
            });
        }

        return urlWithParams;
    }

    /**
     * Reads a query string parameter value from the current page URL via the global `FindURLParameter` helper, stripping any trailing hash fragment from the value.
     * @param queryParam name of the query parameter to look up
     * @returns the parameter value, or null/undefined if not present
     */
    public static findUrlParameter(queryParam): string {
        // could be followed by a hash, so split and return the first part
        let value = window.FindURLParameter(queryParam);

        if (value != null) {
            return value.split("#")[0];
        } else {
            return value;
        }
    }

    /**
     * Appends a new parameter to the current page's URL, updating both the browser history state and any page form's `action` attribute.
     * @param name parameter name to add
     * @param value parameter value to add
     */
    public static addUrlParameter(name, value) {
        let locationObject = this.getLocationObject();
        let searchQueryPart = locationObject.urlSearchQuery;

        // search for the ? mark, add if not found
        // else add a & as there is probably already a parameter
        if (searchQueryPart.length == 0) {
            searchQueryPart += "?";
        } else {
            searchQueryPart += "&";
        }

        // add parameter
        searchQueryPart += name + "=" + value;

        // update browser history state and form post attribute url
        this.modifyUrl(locationObject, searchQueryPart);
    }

    /**
     * Replaces an existing query parameter's value (matched case-insensitively) in the current page's URL, updating browser history state and any page form's `action` attribute.
     * @param name parameter name to update (matched case-insensitively)
     * @param value new value to set
     */
    // case insensitive checking
    public static modifyUrlParameter(name, value) {
        let locationObject = this.getLocationObject();
        let searchQueryPart = locationObject.urlSearchQuery;

        let regex = new RegExp("(" + name + ")([^&]*)", "i");
        searchQueryPart = searchQueryPart.replace(regex, "$1=" + value);

        this.modifyUrl(locationObject, searchQueryPart);
    }

    /**
     * Removes a query parameter (matched case-insensitively) from the current page's URL, updating browser history state and any page form's `action` attribute.
     * @param name parameter name to remove (matched case-insensitively)
     * @returns the result of {@link modifyUrl} applying the updated URL
     */
    // case insensitive checking
    public static removeUrlParameter(name) {
        let locationObject = this.getLocationObject();
        let searchQueryPart = locationObject.urlSearchQuery.substring(1).split("&");

        // build new search query
        let newSearchQueryPart = searchQueryPart.filter(function (item) {
            let keyValueSplit = item.split("=");
            return keyValueSplit[0].toLowerCase() != name.toLowerCase();
        }).join("&");

        if (newSearchQueryPart.length > 0) {
            newSearchQueryPart = "?" + newSearchQueryPart;
        }

        return this.modifyUrl(locationObject, newSearchQueryPart);
    }

    /**
     * Captures the parts of `window.location` relevant to URL manipulation.
     * @returns object with the URL's origin, pathname, search query, and hash
     */
    private static getLocationObject() {
        const locationObject = window.location;
        return {
            urlOrigin: locationObject.origin,
            urlPathName: locationObject.pathname,
            urlSearchQuery: locationObject.search,
            urlHash: locationObject.hash
        };
    }

    /**
     * Rewrites the browser's URL via `history.replaceState` and updates any page form's `action` attribute to match, without triggering navigation.
     * @param locationObject captured location parts (origin, pathname, hash) used to rebuild the URL
     * @param searchQueryPart new query string portion to apply (including a leading `?`, or empty for none)
     */
    private static modifyUrl(locationObject, searchQueryPart) {
        let jForm = jQuery("form");

        let relativeUrl = locationObject.urlPathName + searchQueryPart + locationObject.urlHash;
        window.history.replaceState(null, window.document.title, relativeUrl);

        let completeUrl = locationObject.urlOrigin + relativeUrl;
        jForm.attr("action", completeUrl);
    }
}